//
// 顶部状态栏：连接状态 / 消息数 / 问询控制 / 一键重连
//

import React from "react";
import "./StatusBar.scss";

interface StatusBarProps {
    isConnected: boolean;
    isSessionActive: boolean;
    sessionId: string | null;
    messageCount: number;
    isAudioPlaying?: boolean;
    audioError?: Event | null;
    reconnectAttempts?: number;
    maxReconnectAttempts?: number;
    reconnectExhausted?: boolean;
    phase?: "landing" | "inquiry";
    avatarReady?: boolean;
    avatarError?: string | null;
    onReconnect?: () => void;
    onEndInquiry?: () => void;
    isSessionStarting?: boolean;
}

export const StatusBar: React.FC<StatusBarProps> = ({
    isConnected,
    isSessionActive,
    messageCount,
    isAudioPlaying = false,
    audioError = null,
    reconnectAttempts = 0,
    maxReconnectAttempts = 5,
    reconnectExhausted = false,
    phase = "landing",
    avatarReady = false,
    avatarError = null,
    onReconnect,
    onEndInquiry,
}) => {
    const getStatusColor = (): string => {
        if (!isConnected) return "#ef4444";
        if (audioError || avatarError) return "#f59e0b";
        if (isSessionActive || (phase === "inquiry" && avatarReady))
            return "#22c55e";
        return "#6b7280";
    };

    const getStatusText = (): string => {
        if (!isConnected) {
            if (reconnectExhausted) return "连接失败";
            if (reconnectAttempts > 0) {
                return `重连中 (${reconnectAttempts}/${maxReconnectAttempts})`;
            }
            return "未连接";
        }
        if (avatarError && !avatarReady) {
            if (isSessionActive || phase === "inquiry") return "扬声器播报";
            if (/通道已满|人数较多|并发/.test(avatarError)) return "数字人繁忙";
            return "数字人异常";
        }
        if (phase === "inquiry" && !avatarReady) return "数字人加载中";
        if (isSessionActive || phase === "inquiry") return "问询中";
        return "待机";
    };

    const getStatusIcon = (): string => {
        if (!isConnected) return "🔴";
        if (audioError || avatarError) return "⚠️";
        if (isSessionActive || phase === "inquiry") return "🟢";
        return "⏸️";
    };

    const showReconnect = !isConnected || reconnectExhausted || !!avatarError;

    return (
        <div className="status-bar">
            <div className="status-left">
                <div className="status-item">
                    <span className="status-icon">{getStatusIcon()}</span>
                    <span className="status-label">状态</span>
                    <span
                        className="status-value"
                        style={{ color: getStatusColor() }}
                    >
                        {getStatusText()}
                    </span>
                </div>

                <div className="status-divider" />

                <div className="status-item">
                    <span className="status-icon">💬</span>
                    <span className="status-label">消息</span>
                    <span className="status-value">{messageCount}</span>
                </div>
            </div>

            <div className="status-right">
                <div className="status-item">
                    <span
                        className={`status-dot ${isConnected ? "online" : "offline"} ${!isConnected ? "breathing" : ""}`}
                    />
                    <span
                        className={`status-value ${!isConnected ? "breathing-text" : ""}`}
                        style={{ color: getStatusColor() }}
                    >
                        {isConnected ? "已连接" : "连接中…"}
                    </span>
                </div>

                {isAudioPlaying && (
                    <>
                        <div className="status-divider" />
                        <div className="status-item audio-playing">
                            <span className="status-icon">🎵</span>
                            <span className="status-label">播放中</span>
                            <span className="status-value playing-animation">
                                ●
                            </span>
                        </div>
                    </>
                )}

                {
                    /*
                    {showReconnect && onReconnect && (
                    <>
                        <div className="status-divider" />
                        <button
                            type="button"
                            className="status-action reconnect"
                            onClick={onReconnect}
                        >
                            一键重连
                        </button>
                    </>
                )}
                    */
                }

                {phase === "inquiry" && onEndInquiry && (
                    <>
                        <div className="status-divider" />
                        <button
                            type="button"
                            className="status-action end"
                            onClick={onEndInquiry}
                        >
                            结束问询
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default StatusBar;
